import { useCallback, useMemo } from 'react';
import { useTenant, useAnalytics } from '@parcel2go/npm-p2g-app';
import { fetchWithTenantAnalytics } from '~/lib/fetch';
import { captureException, captureWarning } from '~/lib/metrics';
import { QUOTE_API } from '~/lib/constants';
import { QuoteRequirementResponse, QuoteRequirements } from '~/lib/types/basket-types';

export interface RequirementsApiResult {
  data: QuoteRequirements | null;
  error: Error | null;
}

// Module-level cache and in-flight map shared across every consumer of
// useRequirementsApi. Requirements for a (tenant, slug, origin, destination)
// tuple don't change between page nav events, so we dedupe both concurrent
// in-flight calls and short-window repeat calls. Only successful responses
// are cached; failures fall through so a refresh can retry immediately.
const REQUIREMENTS_CACHE_TTL_MS = 5 * 60 * 1000;
const requirementsCache = new Map<string, { result: RequirementsApiResult; expiresAt: number }>();
const requirementsInFlight = new Map<string, Promise<RequirementsApiResult>>();

const buildCacheKey = (tenant: string, serviceSlug: string, origin: string, destination: string) =>
  `${tenant}|${serviceSlug}|${origin}|${destination}`;

export const clearRequirementsCache = (): void => {
  requirementsCache.clear();
  requirementsInFlight.clear();
};

/**
 * Hook to manage API calls to the requirements endpoint.
 * Exposes a method to fetch quote requirements based on service slug, origin, and destination. (fetchRequirements)
 *
 * Responses are cached at module scope for {@link REQUIREMENTS_CACHE_TTL_MS} per
 * (tenant, slug, origin, destination) tuple, and concurrent calls for the same
 * tuple share a single in-flight promise. This deduplicates the redundant
 * fetches that the basketContext effect and the /new route effect would
 * otherwise both fire with identical params on every checkout entry.
 */
export function useRequirementsApi() {
  const { tenant, settings } = useTenant();
  const { session } = useAnalytics();

  const fetchRequirements = useCallback(
    async (serviceSlug: string, origin: string, destination: string): Promise<RequirementsApiResult> => {
      if (!serviceSlug || !origin || !destination) {
        return {
          data: null,
          error: new Error('serviceSlug, origin, and destination are required for requirements')
        };
      }

      const cacheKey = buildCacheKey(tenant, serviceSlug, origin, destination);
      const cached = requirementsCache.get(cacheKey);
      if (cached && cached.expiresAt > Date.now()) {
        return cached.result;
      }
      const inFlight = requirementsInFlight.get(cacheKey);
      if (inFlight) {
        return inFlight;
      }

      const request = (async (): Promise<RequirementsApiResult> => {
        try {
          const url = `${QUOTE_API}/services/${serviceSlug}/${origin}/${destination}/requirements`;
          const response = await fetchWithTenantAnalytics(tenant, settings, session, url);

          if (response.status === 400) {
            captureWarning('Requirements Invalid', {
              section: 'requirements-api'
            });
            return {
              data: null,
              error: new Error('Requirements invalid - status 400')
            };
          }

          if (!response.ok) {
            throw new Error(`Failed to fetch requirements - invalid http response ${response.status}`);
          }

          const result = (await response.json()) as QuoteRequirementResponse;

          if (!result?.success || !result?.result?.ruleSets) {
            throw new Error(`Failed to fetch requirements - invalid api response: ${result.error?.message}`);
          }

          const success: RequirementsApiResult = {
            data: result.result.ruleSets,
            error: null
          };
          requirementsCache.set(cacheKey, {
            result: success,
            expiresAt: Date.now() + REQUIREMENTS_CACHE_TTL_MS
          });
          return success;
        } catch (error) {
          const err = error instanceof Error ? error : new Error('Unknown error');
          captureException(err, 'Failed to fetch requirements', {
            section: 'requirements-api'
          });

          return {
            data: null,
            error: err
          };
        } finally {
          requirementsInFlight.delete(cacheKey);
        }
      })();

      requirementsInFlight.set(cacheKey, request);
      return request;
    },
    [tenant, settings, session]
  );

  return useMemo(
    () => ({
      fetchRequirements
    }),
    [fetchRequirements]
  );
}
